import { Button, Flex } from '@utilitywarehouse/hearth-react';
import { Formik } from 'formik';
import { ReactNode } from 'react';

import { FormikPersist } from '@components/FormikPersist';
import { ADDRESS_PAGE } from '@constants/formNames';
import { FormSync } from '@features/Remote/Interaction/FormSync';
import { AddressInput } from '@generated/gql/graphql';

import {
  AdditionalAddressPickerValues,
  FormValues,
  NestedFormValues,
} from '../types';
import { validate } from '../validation';
import { AddressForm } from './AddressForm';
import './styles.scss';

interface AddressPickerProps<T> {
  children: ReactNode;
  name: T;
  label?: string;
  isLoading?: boolean;
  onSubmit: (
    address: AddressInput,
    additionalValues: AdditionalAddressPickerValues
  ) => void;
}

const initialValues: FormValues = {
  postcode: '',
  subBuilding: '',
  buildingName: '',
  buildingNumber: '',
  thoroughfare: '',
  postTown: '',
  dependentLocality: '',
  doubleDependentLocality: '',
  dependentThoroughfare: '',
  county: '',
  organisation: '',
  department: '',
  uprn: '',
  addressSelection: '',
  manualEntry: false,
};

const getInitialFormValues = (formName: string): NestedFormValues<string> => ({
  [formName]: initialValues,
});

const AddressPicker = <T extends string>({
  children,
  name,
  isLoading = false,
  label = 'Submit',
  onSubmit,
}: AddressPickerProps<T>) => {
  const handleOnSubmit = (values: NestedFormValues<T>) => {
    const {
      postcode,
      subBuilding,
      buildingName,
      buildingNumber,
      thoroughfare,
      postTown,
      dependentLocality,
      doubleDependentLocality,
      dependentThoroughfare,
      county,
      organisation,
      department,
      uprn,
      manualEntry,
      ...restValues
    } = values[name];

    const addressInput = {
      postcode,
      subBuilding,
      buildingName,
      buildingNumber,
      thoroughfare,
      postTown,
      dependentLocality,
      doubleDependentLocality,
      dependentThoroughfare,
      county,
      organisation,
      department,
      ...(!manualEntry && { uprn }),
    };

    onSubmit(addressInput, { ...restValues, manualEntry });
  };

  return (
    <Formik
      validateOnMount
      initialValues={getInitialFormValues(name)}
      validate={(values) => validate(values[name], name)}
      onSubmit={handleOnSubmit}
    >
      {({ handleSubmit }) => {
        return (
          <>
            <FormSync />

            <FormikPersist name={ADDRESS_PAGE} />
            <form className="addressPicker__form" onSubmit={handleSubmit}>
              <Flex spacing="xl" direction="column">
                <AddressForm isLoading={isLoading} name={name}>
                  {children}
                </AddressForm>
                <Flex
                  className="addressPicker__Submit"
                  direction="column"
                  alignItems={{ mobile: 'stretch', tablet: 'start' }}
                >
                  <Button
                    disabled={isLoading}
                    type="submit"
                    id="AddressPickerSubmit"
                    loading={isLoading}
                  >
                    {label}
                  </Button>
                </Flex>
              </Flex>
            </form>
          </>
        );
      }}
    </Formik>
  );
};

export { AddressPicker };
